import { Directive, OnDestroy, OnInit, inject } from '@angular/core';
import { AbstractItemComponent } from '@df/catalog-ui/abstract-item.component';
import { IFilterDefinition, IListingSetup } from '@df/catalog-ui/listing/listing';
import { IProductListingConfig, ProductListing } from '@df/catalog-ui/listing/product-listing';
import { ProductListingFactory } from '@df/catalog-ui/listing/product-listing.factory';
import { IListingSizeOption } from '@df/catalog-ui/listing/size/listing-size';
import { IListingSortOption } from '@df/catalog-ui/listing/sort/listing-sort';
import { Category, ICategoryDataV2, ICategoryDataV3 } from '@df/catalog/category/category';
import { IProductDataV2, Product } from '@df/catalog/product/product';
import { TNumberPerSize } from '@df/core/df-core.interfaces';
import isEqual from 'lodash-es/isEqual';
import { Subscription } from 'rxjs';
import { NCatalogUi } from '../df-catalog-ui.config';

@Directive()
export class CategoryComponentDef<
        T extends Category<ICategoryDataV2> = Category<ICategoryDataV2>,
        D extends Product<IProductDataV2> = Product<IProductDataV2>
    >
    extends AbstractItemComponent<T>
    implements OnInit, OnDestroy
{
    type = Category.type;

    /**
     * Listing model
     */
    listing: ProductListing<D> | undefined;

    /**
     * Listing model subscription
     */
    listingSubscription?: Subscription;

    protected productListingFactory = inject(ProductListingFactory);

    private lastListingConfig?: IProductListingConfig;
    private lastProductIds?: number[];
    private lastListingSetup?: IListingSetup<D>;

    protected moduleConfig = inject(NCatalogUi.OPTIONS);

    /**
     * Creates new listing on model change
     */
    protected override onModelChange(): void {
        super.onModelChange();

        this.unsubscribe();

        if (this.model) {
            this.resetListing();
        } else {
            this.listing = undefined;
        }
    }

    protected override onModelDataChange(): void {
        this.resetListing();
        super.onModelDataChange();
    }

    protected resetListing(): void {
        const listingConfig = this.getListingConfig();
        const productIds = this.getProductIds();
        const listingSetup = this.getListingSetup();
        if (
            this.listing &&
            isEqual(listingConfig, this.lastListingConfig) &&
            isEqual(productIds, this.lastProductIds) &&
            isEqual(listingSetup, this.lastListingSetup)
        ) {
            return;
        }
        this.lastListingConfig = listingConfig;
        this.lastProductIds = productIds;
        this.lastListingSetup = listingSetup;
        this.unsubscribe();
        this.listing = this.getListing(listingConfig, productIds, listingSetup);

        this.listingSubscription = this.listing.subscribe(() => this.detectChanges());
    }

    /**
     * Cleanup
     */
    override ngOnDestroy(): void {
        super.ngOnDestroy();
        this.unsubscribe();
    }

    protected getListing(config: IProductListingConfig, ids: number[], setup: IListingSetup<D>): ProductListing<D> {
        return this.productListingFactory.create<D>(config, ids, setup);
    }

    protected getAppConfig() {
        return this.moduleConfig.listing.category;
    }

    protected getListingConfig(): IProductListingConfig {
        const config = this.getAppConfig();
        return {
            type: 'product',
            source: this.getListingSource(),
            infScrollStep: config.infScrollStep,
            infScrollInit: config.infScrollInit,
            pushOOStoBottom: (this.model!.data as ICategoryDataV3).push_oos_to_bottom,
            storage: true,
            custom: {
                pioAllowed: true,
                isMain: true
            }
        };
    }

    protected getListingSource(): any {
        return this.model;
    }

    private getListingSetup(): IListingSetup<D> {
        return {
            filtersDefinitions: this.getFilterDefs(),
            sortOptions: this.getSortOps(),
            sizeOptions: this.getSizeOps(),
            defaultSize: this.getDefaultSize()
        };
    }

    /**
     * Unsubscribes listing changes if subscription exists
     */
    protected unsubscribe(): void {
        this.listing?.clearSubscriptions();
        this.listingSubscription?.unsubscribe();
    }

    /**
     * Filters provider for listing
     */
    protected getFilterDefs(): (IFilterDefinition | string)[] {
        if (this.model) {
            const data = <ICategoryDataV3>this.model.data;
            if (data && data.filters) {
                return data.filters;
            }
        }
        return this.app.resources.attribute
            .getAllExisting()
            .filter(i => !!i.data!.filterable)
            .map(i => i.id);
    }

    /**
     * Sort options provider for listing
     */
    protected getSortOps(): IListingSortOption[] | undefined {
        return this.getAppConfig().sort;
    }

    /**
     * Size options provider for listing
     */
    protected getSizeOps(): IListingSizeOption[] | undefined {
        return this.getAppConfig().size;
    }

    /**
     * Default size options
     */
    protected getDefaultSize(): TNumberPerSize | undefined {
        return this.getAppConfig().defaultSize;
    }

    /**
     * Product ids privider for listing
     */
    protected getProductIds(): number[] {
        return (this.model?.data as ICategoryDataV3)?.products || [];
    }
}
